import React, { useCallback } from "react";
import styles from "./Profile.module.css";
import ghostStyles from "./ProfileGhostMode.module.css";
import avatarDefault from "../icons/avatar.png";
import { HapticFeedback } from "../../utils/hapticFeedback";
import { Portal } from "../Portal/Portal";
import { useBackButton } from "../../hooks/useBackButton";
import { YandexMetrika } from "../../utils/yandexMetrika";
import { useUnreadCount } from "../../hooks/useNotifications";
import { useAppSelector, useAppDispatch } from "../../hooks/redux";
import { Toggle } from "../Toggle/Toggle";
import { GhostModeIcon } from "../icons/GhostModeIcon";
import { setUserData } from "../../store/slices/mapSlice";
import { apiClient } from "../../api/client";
import { useTheme } from "../../hooks/useTheme";

interface ProfileProps {
  onChangeCity: () => void;
  isPremium?: boolean;
  onClose: () => void;
  settingsOpen?: boolean;
  onNotificationsClick?: () => void;
  onNetworkingClick?: () => void;
  onGiftSubscriptionClick?: () => void;
}

export const Profile: React.FC<ProfileProps> = ({
  onChangeCity,
  isPremium = false,
  onClose,
  settingsOpen = false,
  onNotificationsClick,
  onNetworkingClick,
  onGiftSubscriptionClick,
}) => {
  const dispatch = useAppDispatch();
  const { unreadCount } = useUnreadCount(true);
  const selectedCity = useAppSelector((state) => state.map.selectedCity);
  const userData = useAppSelector((state) => state.map.userData);
  const realUserCache = useAppSelector((state) => state.map.realUserCache);
  const ghostUserCache = useAppSelector((state) => state.map.ghostUserCache);
  const { theme } = useTheme();

  const isNetworkingEnabled =
    selectedCity?.settings?.is_networking_enabled ?? false;

  useBackButton({
    isOpen: !settingsOpen,
    onBack: onClose,
  });

  const handleSettingsClick = () => {
    HapticFeedback.medium();
    YandexMetrika.goToSettings();
    onChangeCity();
  };

  const handleNotificationsClick = () => {
    HapticFeedback.medium();
    if (onNotificationsClick) {
      onNotificationsClick();
    }
  };

  const handleNetworkingClick = () => {
    HapticFeedback.medium();
    if (onNetworkingClick) {
      onNetworkingClick();
    }
  };

  const handleSupportClick = () => {
    HapticFeedback.medium();
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.openTelegramLink("https://t.me/dpscontrol_help");
    } else {
      window.open("https://t.me/dpscontrol_help", "_blank");
    }
  };

  const handleGiftSubscriptionClick = () => {
    HapticFeedback.medium();
    if (onGiftSubscriptionClick) {
      onGiftSubscriptionClick();
    }
  };

  const handleGhostModeToggle = useCallback(async () => {
    try {
      const newValue = !userData?.ghost_mode_enabled;
      const cached = newValue ? ghostUserCache : realUserCache;
      if (cached) {
        dispatch(setUserData(cached));
        apiClient.saveSettings("ghost-mode", newValue.toString());
        return;
      }
      await apiClient.saveSettings("ghost-mode", newValue.toString());
      const data = await apiClient.getAppData();
      if (data?.user) {
        dispatch(setUserData(data.user));
      }
    } catch (error) {
      console.error("❌ Ошибка переключения ghost mode:", error);
    }
  }, [dispatch, userData?.ghost_mode_enabled, realUserCache, ghostUserCache]);

  return (
    <Portal>
      <div className={styles.overlay}>
        <div className={styles.container}>
          <div className={styles.header}>
            {/* Кнопка уведомлений */}
            <button
              className={styles.headerNotificationsButton}
              onClick={handleNotificationsClick}
            >
              <div className={styles.notificationIcon}>
                <svg
                  width="18"
                  height="20"
                  viewBox="0 0 18 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.99549 15.75C5.99549 16.5456 6.31156 17.3087 6.87417 17.8713C7.43678 18.4339 8.19984 18.75 8.99549 18.75C9.79114 18.75 10.5542 18.4339 11.1168 17.8713C11.6794 17.3087 11.9955 16.5456 11.9955 15.75M2.24549 7.5C2.24549 5.70979 2.95665 3.9929 4.22252 2.72703C5.48839 1.46116 7.20528 0.75 8.99549 0.75C10.7857 0.75 12.5026 1.46116 13.7685 2.72703C15.0343 3.9929 15.7455 5.70979 15.7455 7.5C15.7455 10.8581 16.5236 13.5563 17.1424 14.625C17.2081 14.7388 17.2427 14.8679 17.2428 14.9993C17.243 15.1308 17.2085 15.2599 17.143 15.3739C17.0775 15.4878 16.9832 15.5825 16.8696 15.6485C16.7559 15.7145 16.6269 15.7495 16.4955 15.75H1.49549C1.36422 15.7492 1.23546 15.714 1.12207 15.6478C1.00868 15.5817 0.914644 15.4869 0.849352 15.3731C0.78406 15.2592 0.749801 15.1301 0.750001 14.9989C0.7502 14.8676 0.784851 14.7387 0.850489 14.625C1.4683 13.5563 2.24549 10.8572 2.24549 7.5Z"
                    stroke="var(--tg-theme-text-color, #141218)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {unreadCount > 0 && (
                  <div className={styles.notificationBadge} />
                )}
              </div>
            </button>

            <div className={styles.avatarWrapper}>
              <div className={styles.avatar}>
                <img
                  src={userData?.photo_url || avatarDefault}
                  alt="Аватар пользователя"
                  className={styles.avatarImage}
                />
              </div>

              {isPremium && <div className={styles.premiumBadge}>Premium</div>}
            </div>

            <h2 className={styles.userName}>
              {userData?.name || "Пользователь"}
            </h2>

            {/* Кнопка настроек в заголовке */}
            <button
              className={styles.headerSettingsButton}
              onClick={handleSettingsClick}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.75032 14.8048C12.542 14.8048 14.8052 12.5417 14.8052 9.74993C14.8052 6.9582 12.542 4.69505 9.75032 4.69505C6.95859 4.69505 4.69545 6.9582 4.69545 9.74993C4.69545 12.5417 6.95859 14.8048 9.75032 14.8048Z"
                  stroke="var(--tg-theme-text-color, #141218)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17.6934 9.02788L18.6238 7.63234C18.6844 7.54244 18.7245 7.44029 18.7412 7.33314C18.7579 7.226 18.7508 7.11649 18.7204 7.01239C18.5449 6.43443 18.3136 5.87491 18.0297 5.34173C17.9777 5.24682 17.9054 5.16456 17.8179 5.10082C17.7305 5.03708 17.63 4.99344 17.5237 4.97301L15.8775 4.64373L14.8563 3.62251L14.527 1.97619C14.5066 1.8699 14.4629 1.76944 14.3992 1.68197C14.3354 1.5945 14.2532 1.52219 14.1582 1.47019C13.625 1.18637 13.0655 0.955082 12.4875 0.779583C12.3834 0.749203 12.2739 0.742113 12.1668 0.758816C12.0596 0.77552 11.9575 0.815607 11.8676 0.876231L10.4721 1.80653H9.02788L7.63243 0.876231C7.54252 0.815607 7.44037 0.77552 7.33323 0.758817C7.22608 0.742113 7.11658 0.749203 7.01248 0.779583C6.43451 0.955081 5.87498 1.18637 5.34178 1.47018C5.24686 1.52218 5.16458 1.5945 5.10084 1.68198C5.03709 1.76945 4.99345 1.86992 4.97303 1.97621L4.64383 3.62242L3.62252 4.64373L1.97629 4.97293C1.87 4.99335 1.76954 5.03699 1.68207 5.10073C1.5946 5.16447 1.52229 5.24674 1.47029 5.34166C1.18642 5.87486 0.955099 6.43441 0.779576 7.0124C0.7492 7.11649 0.742113 7.226 0.75882 7.33314C0.775526 7.44028 0.815615 7.54243 0.876241 7.63233L1.80654 9.02779V10.472L0.876237 11.8675C0.815615 11.9574 0.775529 12.0596 0.758825 12.1667C0.742122 12.2738 0.74921 12.3833 0.779588 12.4874C0.955061 13.0654 1.18635 13.625 1.47019 14.1582C1.52219 14.2531 1.59451 14.3354 1.68197 14.3991C1.76944 14.4628 1.8699 14.5065 1.97618 14.5269L3.62252 14.8562L4.64374 15.8774L4.97294 17.5237C4.99337 17.63 5.03701 17.7305 5.10074 17.818C5.16448 17.9054 5.24674 17.9777 5.34166 18.0297C5.87483 18.3136 6.43436 18.5449 7.01234 18.7204C7.11642 18.7507 7.22592 18.7578 7.33306 18.7411C7.44019 18.7244 7.54233 18.6843 7.63223 18.6237L9.02779 17.6934H10.472L11.8676 18.6238C11.9575 18.6844 12.0596 18.7245 12.1668 18.7412C12.2739 18.7579 12.3834 18.7508 12.4875 18.7204C13.0655 18.5449 13.625 18.3137 14.1582 18.0298C14.2531 17.9778 14.3353 17.9055 14.3991 17.818C14.4628 17.7306 14.5065 17.6301 14.5269 17.5238L14.8562 15.8775L15.8774 14.8562L17.5237 14.527C17.63 14.5066 17.7305 14.4629 17.8179 14.3992C17.9054 14.3354 17.9777 14.2532 18.0297 14.1583C18.3136 13.6251 18.5449 13.0656 18.7204 12.4876C18.7508 12.3835 18.7579 12.274 18.7412 12.1669C18.7245 12.0597 18.6844 11.9576 18.6237 11.8677L17.6934 10.4721V9.02788Z"
                  stroke="var(--tg-theme-text-color, #141218)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className={styles.menuList}>
            {isPremium && (
              <div className={ghostStyles.ghostModeCard}>
                <div className={ghostStyles.ghostModeContent}>
                  <GhostModeIcon width={24} height={24} color="currentColor" />
                  <div className={ghostStyles.ghostModeText}>
                    Режим невидимки
                  </div>
                </div>
                <Toggle
                  isActive={userData?.ghost_mode_enabled ?? false}
                  onClick={handleGhostModeToggle}
                  theme={theme}
                />
              </div>
            )}

            {isNetworkingEnabled && (
              <button
                className={styles.menuItem}
                onClick={handleNetworkingClick}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.8979 14.2497C15.5026 14.9335 14.9343 15.5013 14.2501 15.896C13.5659 16.2908 12.7899 16.4986 12 16.4986C11.2101 16.4986 10.4341 16.2908 9.74993 15.8961C9.06574 15.5013 8.49743 14.9335 8.10205 14.2497M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="var(--colors-icons-primary, #141218)"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8.625 11.25C9.24632 11.25 9.75 10.7463 9.75 10.125C9.75 9.50368 9.24632 9 8.625 9C8.00368 9 7.5 9.50368 7.5 10.125C7.5 10.7463 8.00368 11.25 8.625 11.25Z"
                    fill="var(--colors-icons-primary, #141218)"
                  />
                  <path
                    d="M15.375 11.25C15.9963 11.25 16.5 10.7463 16.5 10.125C16.5 9.50368 15.9963 9 15.375 9C14.7537 9 14.25 9.50368 14.25 10.125C14.25 10.7463 14.7537 11.25 15.375 11.25Z"
                    fill="var(--colors-icons-primary, #141218)"
                  />
                </svg>
                <span>Профиль нетворкинга</span>
                <svg
                  className={styles.chevronIcon}
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 4L10 8L6 12"
                    stroke="var(--colors-icons-tertiary, #D1CFD8)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}

            <button
              className={styles.menuItem}
              onClick={handleGiftSubscriptionClick}
            >
              <svg
                width="19"
                height="18"
                viewBox="0 0 19 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17 9.90075V16.6507C17 16.8497 16.921 17.0404 16.7803 17.1811C16.6397 17.3217 16.4489 17.4007 16.25 17.4007H2.75C2.55109 17.4007 2.36032 17.3217 2.21967 17.1811C2.07902 17.0404 2 16.8497 2 16.6507V9.90075M9.5 5.40074V17.4007M9.5 5.40074C9.5 5.40074 12.682 5.40074 13.7426 4.34008C14.1642 3.91804 14.4009 3.34588 14.4007 2.74936C14.4006 2.15284 14.1635 1.58081 13.7417 1.15901C13.3199 0.73721 12.7479 0.50017 12.1514 0.5C11.5549 0.49983 10.9827 0.736545 10.5607 1.1581C9.5 2.21876 9.5 5.40074 9.5 5.40074ZM9.5 5.40074C9.5 5.40074 6.31802 5.40074 5.25736 4.34008C4.8358 3.91804 4.59909 3.34588 4.59926 2.74936C4.59942 2.15284 4.83647 1.58081 5.25827 1.15901C5.68007 0.73721 6.2521 0.50017 6.84862 0.5C7.44513 0.49983 8.0173 0.736545 8.43934 1.1581C9.5 2.21876 9.5 5.40074 9.5 5.40074ZM1.25 5.40074H17.75C18.1642 5.40074 18.5 5.73653 18.5 6.15074V9.15075C18.5 9.56496 18.1642 9.90075 17.75 9.90075H1.25C0.835786 9.90075 0.5 9.56496 0.5 9.15075V6.15074C0.5 5.73653 0.835786 5.40074 1.25 5.40074Z"
                  stroke="var(--colors-icons-primary, #141218)"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Подарить подписку</span>
              <svg
                className={styles.chevronIcon}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 4L10 8L6 12"
                  stroke="var(--colors-icons-tertiary, #D1CFD8)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <button className={styles.menuItem} onClick={handleSupportClick}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z"
                  stroke="var(--colors-icons-primary, #141218)"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 15.75C14.0711 15.75 15.75 14.0711 15.75 12C15.75 9.92893 14.0711 8.25 12 8.25C9.92893 8.25 8.25 9.92893 8.25 12C8.25 14.0711 9.92893 15.75 12 15.75Z"
                  stroke="var(--colors-icons-primary, #141218)"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.34922 9.34873L5.63672 5.63623"
                  stroke="var(--colors-icons-primary, #141218)"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14.6523 9.34873L18.3648 5.63623"
                  stroke="var(--colors-icons-primary, #141218)"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14.6523 14.6512L18.3648 18.3637"
                  stroke="var(--colors-icons-primary, #141218)"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.34922 14.6512L5.63672 18.3637"
                  stroke="var(--colors-icons-primary, #141218)"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Написать в техподдержку</span>
              <svg
                className={styles.chevronIcon}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 4L10 8L6 12"
                  stroke="var(--colors-icons-tertiary, #D1CFD8)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* Карточка с лошадиными силами */}
          {/* <div className={styles.statsCard}>
            <div className={styles.statsHeader}>
              <span className={styles.statsTitle}>Лошадиные силы</span>
              <span className={styles.statsIcon}>⚡</span>
            </div>
            <div className={styles.statsNumber}>0</div>
          </div> */}

          {/* Информационная карточка */}
          {/* <div className={styles.infoCard}>
            <p className={styles.infoText}>
              Чем активнее вы на дороге и в приложении, тем больше лошадиных
              сил. Зарабатывайте и обменивайте их на реальные призы
            </p>
            <button className={styles.infoButton}>Как получить л.с?</button>
          </div> */}

          {/* Настройки */}
          {/* <div className={styles.settingsSection}>
            <button
              className={styles.settingsButton}
              onClick={handleSettingsClick}
            >
              <div className={styles.settingsIcon}>
                <img
                  src={settingsIcon}
                  alt="Настройки"
                  width={24}
                  height={24}
                />
              </div>
              <span>Настройки</span>
              <svg
                className={styles.chevronIcon}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div> */}
        </div>
      </div>
    </Portal>
  );
};
